<template>
    <v-row>
        <v-col>
            <v-col cols="12">
                <v-row class="d-flex align-center justify-content-between">
                    <v-col cols="auto">
                        <h3 class="pl-3" v-text="$t('praxisgruppe.einstellungen.benutzer.list.title')" />
                    </v-col>
                    <v-spacer />
                    <v-col cols="auto">
                        <AddAccountDialog />
                    </v-col>
                </v-row>
            </v-col>
            <v-data-table
                v-model:expanded="expanded"
                v-model:sort-by="sortBy"
                :headers="headers"
                :items="benutzerList"
                item-value="id"
                show-expand
                class="elevation-1"
                single-expand
                disable-pagination
                hide-default-footer
                :must-sort="true"
                hover
                :items-per-page="-1"
                @click:row="expandRow"
            >
                <!-- Selbst definierte Tablespalten -->
                <template #item.burgermenu="{ item }">
                    <v-menu v-model="item.burgermenuActive" offset-x>
                        <template #activator="{ props }">
                            <v-btn icon flat :href="item.burgermenu" v-bind="props">
                                <v-icon color="primary" icon="mdi-menu" />
                            </v-btn>
                        </template>
                        <v-list>
                            <EditAccountDialog :edit-benutzer-id="item.id" @success="expanded = []" />
                            <ConfirmDialog title="einstellungen.deletedialog.title" text="einstellungen.deletedialog.text" @confirm="deleteBenutzer(item.id)">
                                <template #activatorElement="{ props }">
                                    <v-list-item v-bind="props">
                                        <v-list-item-title v-text="$t('table.burgermenu.delete')" />
                                    </v-list-item>
                                </template>
                            </ConfirmDialog>
                            <ConfirmDialog title="einstellungen.resetmfa.title" text="einstellungen.resetmfa.text" @confirm="resetMfa(item.id)">
                                <template #activatorElement="{ props }">
                                    <v-list-item v-bind="props">
                                        <v-list-item-title v-text="$t('table.burgermenu.resetmfa')" />
                                    </v-list-item>
                                </template>
                            </ConfirmDialog>
                        </v-list>
                    </v-menu>
                </template>

                <!-- Override default expand button -->
                <template #item.data-table-expand="{ item }">
                    <v-btn icon class="elevation-0">
                        <v-icon>{{ isRowExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    </v-btn>
                </template>

                <template #expanded-row="{}">
                    <tr class="bg-surface-light py-1">
                        <!-- Spacer column for visually aligning rows -->
                        <td class="d-none d-md-table-cell"></td>

                        <!-- Expanded row content -->
                        <td colspan="100" class="px-4 py-2">
                            <v-row class="d-flex flex-wrap">
                                <!-- Rollen Information -->
                                <v-col cols="12" md="6" class="mb-2">
                                    <span>{{ $t('einstellungen.benutzer.rollen') + ' ' }}</span>
                                    <span v-if="benutzerDetails.rollen">
                                        {{ $t('benutzerrollen.' + benutzerDetails.rollen.toString().toLowerCase()) }}
                                    </span>
                                    <span v-else>-</span>
                                </v-col>

                                <!-- Mobile Number Information -->
                                <v-col cols="12" md="6" class="mb-2">
                                    <span>{{ $t('einstellungen.benutzer.mobile') + ' ' }}</span>
                                    <span v-if="benutzerDetails.mobilNummer">
                                        {{ benutzerDetails.mobilNummer }}
                                    </span>
                                    <span v-else>-</span>
                                </v-col>
                            </v-row>
                        </td>
                    </tr>
                </template>
            </v-data-table>
        </v-col>
    </v-row>
</template>
<script lang="ts">
import { Component, Prop, Vue } from 'vue-facing-decorator';
import { namespace } from 'vuex-facing-decorator';
import { TeilnehmerBenutzerDto } from '@/types/UserDtos';
import { NAME as NAME_PRAXISGRUPPEADMINISTRATION } from '../store';
import { GET_BENUTZER_DETAILS, DELETE_ACCOUNT, RESET_MFA } from '../store/actions';
import { GETBENUTZERDETAILS } from '../store/getters';
import PraxisgruppeEditAccountDialog from './PraxisgruppeEditAccountDialog.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import PraxisgruppeAddAccountDialog from './PraxisgruppeAddAccountDialog.vue';
import { BenutzerDetailApiDto } from '@/api-schema';

const praxisgruppeAdministrationModul = namespace(NAME_PRAXISGRUPPEADMINISTRATION);

@Component({
    components: {
        EditAccountDialog: PraxisgruppeEditAccountDialog,
        ConfirmDialog,
        AddAccountDialog: PraxisgruppeAddAccountDialog
    }
})
export default class PraxisgruppeBenutzerListe extends Vue {
    @Prop({ required: true, type: Array })
    private benutzerList!: Array<TeilnehmerBenutzerDto>;

    @praxisgruppeAdministrationModul.Action(GET_BENUTZER_DETAILS) getBenutzerDetails!: Function;
    @praxisgruppeAdministrationModul.Action(DELETE_ACCOUNT) deleteBenutzer!: Function;
    @praxisgruppeAdministrationModul.Action(RESET_MFA) resetMfa!: Function;
    @praxisgruppeAdministrationModul.Getter(GETBENUTZERDETAILS)
    benutzerDetails!: BenutzerDetailApiDto;

    expanded: Array<TeilnehmerBenutzerDto> = [];
    private itemExpanded(teilnehmer: any) {
        this.getBenutzerDetails(teilnehmer.item.id);
    }
    public expandRow(event: PointerEvent, row: any) {
        if (this.expanded.includes(row.item.id)) {
            this.expanded = [];
        } else {
            this.getBenutzerDetails(row.item.id);
            this.expanded = [];
            this.expanded.push(row.item.id);
        }
    }

    isRowExpanded(row) {
        return this.expanded.includes(row.id);
    }

    sortBy: [{ key: 'email'; order: 'desc' }];

    get headers(): Array<any> {
        return [
            { title: '', key: 'data-table-expand', sortable: false }, // Expand button
            {
                title: this.$t('einstellungen.benutzer.table.id'),
                key: 'email'
            },
            {
                title: this.$t('einstellungen.benutzer.table.name'),
                key: 'nachname'
            },
            {
                title: this.$t('einstellungen.benutzer.table.vorname'),
                key: 'vorname'
            },
            {
                title: this.$t('einstellungen.benutzer.table.action'),
                key: 'burgermenu',
                align: 'center',
                width: '10',
                sortable: false
            }
        ];
    }
}
</script>
